import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { Briefcase, Plus, Trash2, ExternalLink, Users, Edit3, Save, Star, Zap, Download, Search, X } from "lucide-react";

const EMPTY_JOB = {
  title: "", description: "", department: "", subject: "",
  employment_type: "full-time", work_mode: "on-site",
  location_city: "", location_state: "", location_country: "India",
  salary_min: null, salary_max: null, salary_currency: "INR", salary_period: "year",
  application_url: "", application_email: "", apply_deadline: "",
  employer_type: "university", employer_name: "", employer_logo: "", employer_website: "",
  is_featured: false, is_sponsored: false, status: "active",
};

export default function JobsAdminPanel() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filters, setFilters] = useState({ status: "all", employer_type: "", search: "" });
  const [selected, setSelected] = useState(new Set());
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [f, setF] = useState(EMPTY_JOB);
  const [busy, setBusy] = useState(false);
  const [apps, setApps] = useState([]);
  const [viewApps, setViewApps] = useState(false);

  const load = () => {
    const p = { ...filters };
    if (!p.employer_type) delete p.employer_type;
    if (!p.search) delete p.search;
    api.get("/admin/jobs", { params: p }).then(r => { setItems(r.data.items); setCounts(r.data.counts); });
  };
  useEffect(() => { load(); }, [filters]);

  const openCreate = () => { setEditingId(null); setF(EMPTY_JOB); setCreating(true); };
  const openEdit = (j) => { setEditingId(j.id); setF({ ...EMPTY_JOB, ...j }); setCreating(true); };
  const cancel = () => { setCreating(false); setEditingId(null); setF(EMPTY_JOB); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...f,
        salary_min: f.salary_min ? Number(f.salary_min) : null,
        salary_max: f.salary_max ? Number(f.salary_max) : null,
      };
      if (editingId) { await api.put(`/jobs/${editingId}`, payload); toast.success("Job updated"); }
      else { await api.post("/jobs", payload); toast.success("Job created"); }
      cancel(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const patch = async (id, updates) => {
    try { await api.patch(`/admin/jobs/${id}`, updates); load(); }
    catch { toast.error("Update failed"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this job? This is permanent.")) return;
    await api.delete(`/jobs/${id}`); toast.success("Deleted"); load();
  };

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map(i => i.id)));
  };

  const bulk = async (action) => {
    if (!selected.size) return;
    const label = { activate: "activate", expire: "mark expired", feature: "feature", unfeature: "un-feature", delete: "DELETE permanently" }[action];
    if (!confirm(`${label} ${selected.size} job${selected.size > 1 ? "s" : ""}?`)) return;
    try {
      const { data } = await api.post("/admin/jobs/bulk", { ids: Array.from(selected), action });
      toast.success(`Affected ${data.affected}`);
      setSelected(new Set()); load();
    } catch { toast.error("Bulk action failed"); }
  };

  const openAppsView = async () => {
    setViewApps(true);
    const { data } = await api.get("/admin/applications");
    setApps(data);
  };

  if (creating) {
    return (
      <form onSubmit={submit} className="card-soft p-6 space-y-4" data-testid="admin-job-form">
        <div className="flex items-center gap-2 text-sky-700 mb-2"><Briefcase className="w-4 h-4"/><span className="overline">{editingId ? "Edit job" : "New job"}</span></div>
        <input required placeholder="Job title" value={f.title} onChange={e => setF({...f, title: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 font-editorial text-lg" data-testid="admin-job-title"/>
        <textarea required rows={7} placeholder="Description (HTML allowed)" value={f.description} onChange={e => setF({...f, description: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 font-mono text-sm" data-testid="admin-job-desc"/>
        <div className="grid md:grid-cols-3 gap-3">
          <input required placeholder="Employer name" value={f.employer_name} onChange={e => setF({...f, employer_name: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <select value={f.employer_type} onChange={e => setF({...f, employer_type: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 bg-white">
            <option value="university">University</option><option value="college">College</option><option value="school">School</option><option value="edtech">EdTech</option><option value="supplier">Supplier</option><option value="government">Government</option>
          </select>
          <input placeholder="Subject" value={f.subject} onChange={e => setF({...f, subject: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <input placeholder="Employer logo URL" value={f.employer_logo} onChange={e => setF({...f, employer_logo: e.target.value})} className="md:col-span-2 px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <select value={f.status} onChange={e => setF({...f, status: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 bg-white">
            <option value="active">Active</option><option value="draft">Draft</option><option value="expired">Expired</option>
          </select>
          <select value={f.employment_type} onChange={e => setF({...f, employment_type: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 bg-white">
            <option value="full-time">Full-time</option><option value="part-time">Part-time</option><option value="contract">Contract</option><option value="temporary">Temporary</option><option value="internship">Internship</option>
          </select>
          <select value={f.work_mode} onChange={e => setF({...f, work_mode: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 bg-white">
            <option value="on-site">On-site</option><option value="hybrid">Hybrid</option><option value="remote">Remote</option>
          </select>
          <input placeholder="City" value={f.location_city} onChange={e => setF({...f, location_city: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <input placeholder="State" value={f.location_state} onChange={e => setF({...f, location_state: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <input type="number" placeholder="Salary min (₹)" value={f.salary_min || ""} onChange={e => setF({...f, salary_min: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <input type="number" placeholder="Salary max (₹)" value={f.salary_max || ""} onChange={e => setF({...f, salary_max: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <input type="date" placeholder="Deadline" value={f.apply_deadline} onChange={e => setF({...f, apply_deadline: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <input placeholder="Apply URL" value={f.application_url} onChange={e => setF({...f, application_url: e.target.value})} className="md:col-span-2 px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <input placeholder="Apply email" value={f.application_email} onChange={e => setF({...f, application_email: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        </div>
        <div className="flex gap-6 text-sm">
          <label className="flex items-center gap-2 text-slate-700"><input type="checkbox" checked={f.is_featured} onChange={e => setF({...f, is_featured: e.target.checked})}/>Featured</label>
          <label className="flex items-center gap-2 text-slate-700"><input type="checkbox" checked={f.is_sponsored} onChange={e => setF({...f, is_sponsored: e.target.checked})}/>Sponsored</label>
        </div>
        <div className="flex gap-2">
          <button disabled={busy} className="px-6 py-3 rounded-full bg-sky-700 text-white font-semibold text-sm flex items-center gap-1 disabled:opacity-60" data-testid="admin-job-save-btn">{editingId ? <Save className="w-4 h-4"/> : <Plus className="w-4 h-4"/>}{busy?"Saving…":editingId?"Save":"Create"}</button>
          <button type="button" onClick={cancel} className="px-4 py-3 rounded-full bg-slate-100 text-slate-700 text-sm">Cancel</button>
        </div>
      </form>
    );
  }

  if (viewApps) {
    return (
      <div data-testid="admin-apps-view">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-editorial text-xl font-bold">All applications ({apps.length})</h3>
            <p className="text-sm text-slate-500">Latest submissions across every job on the platform</p>
          </div>
          <div className="flex gap-2">
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/applications.csv`} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-full bg-slate-100 hover:bg-sky-100 text-slate-800 text-sm font-semibold flex items-center gap-1" data-testid="apps-csv-download"><Download className="w-4 h-4"/>Export CSV</a>
            <button onClick={() => setViewApps(false)} className="px-4 py-2 rounded-full bg-slate-100 text-slate-700 text-sm">Back to jobs</button>
          </div>
        </div>
        <div className="card-soft overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/70 border-b border-slate-100 text-left">
              <tr><th className="px-5 py-3">Applicant</th><th className="px-5 py-3">Applied for</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">CV</th><th className="px-5 py-3">When</th></tr>
            </thead>
            <tbody>
              {apps.map(a => (
                <tr key={a.id} className="border-b border-slate-50" data-testid={`app-row-${a.id}`}>
                  <td className="px-5 py-3 font-editorial font-semibold text-slate-900">{a.name}</td>
                  <td className="px-5 py-3 text-xs text-slate-600">{a.job_title}<div className="text-slate-400">{a.employer_name}</div></td>
                  <td className="px-5 py-3 text-xs"><a href={`mailto:${a.email}`} className="text-sky-700">{a.email}</a></td>
                  <td className="px-5 py-3 text-xs">{a.cv_url ? <a href={a.cv_url} target="_blank" rel="noreferrer" className="text-sky-700">Open</a> : "—"}</td>
                  <td className="px-5 py-3 text-xs text-slate-500">{formatDate(a.created_at)}</td>
                </tr>
              ))}
              {apps.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-500">No applications yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-jobs-panel">
      <div className="flex flex-wrap items-center gap-2">
        {["all","active","draft","expired"].map(s => (
          <button key={s} onClick={() => setFilters(f => ({...f, status: s}))} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${filters.status === s ? "bg-sky-700 text-white" : "bg-slate-100 text-slate-700"}`} data-testid={`admin-jobs-filter-${s}`}>
            {s} <span className="opacity-70">({counts[s] ?? 0})</span>
          </button>
        ))}
        <div className="ml-auto flex flex-wrap gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={filters.search} onChange={e => setFilters(f => ({...f, search: e.target.value}))} placeholder="Search title / employer / subject…" className="pl-8 pr-3 py-2 rounded-full bg-slate-100 focus:bg-white outline-none text-xs w-72" data-testid="admin-jobs-search"/>
          </div>
          <button onClick={openAppsView} className="px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold flex items-center gap-1" data-testid="admin-apps-btn"><Users className="w-4 h-4"/>All Applications</button>
          <button onClick={openCreate} className="px-4 py-2 rounded-full bg-sky-700 text-white text-sm font-semibold flex items-center gap-1" data-testid="admin-new-job-btn"><Plus className="w-4 h-4"/>New Job</button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="rounded-2xl bg-sky-50 border border-sky-100 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="bulk-bar">
          <span className="text-sm text-sky-800 font-semibold">{selected.size} selected</span>
          <button onClick={() => bulk("activate")} className="px-3 py-1.5 rounded-full bg-white text-slate-700 text-xs font-semibold border border-slate-200 hover:bg-emerald-50">Activate</button>
          <button onClick={() => bulk("expire")} className="px-3 py-1.5 rounded-full bg-white text-slate-700 text-xs font-semibold border border-slate-200 hover:bg-amber-50">Mark Expired</button>
          <button onClick={() => bulk("feature")} className="px-3 py-1.5 rounded-full bg-white text-slate-700 text-xs font-semibold border border-slate-200 hover:bg-sky-50">Feature</button>
          <button onClick={() => bulk("unfeature")} className="px-3 py-1.5 rounded-full bg-white text-slate-700 text-xs font-semibold border border-slate-200">Un-feature</button>
          <button onClick={() => bulk("delete")} className="px-3 py-1.5 rounded-full bg-red-50 text-red-700 text-xs font-semibold border border-red-100 hover:bg-red-100">Delete</button>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-xs text-slate-500 flex items-center gap-1"><X className="w-3 h-3"/>Clear</button>
        </div>
      )}

      <div className="card-soft overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/70 border-b border-slate-100 text-left">
            <tr>
              <th className="px-4 py-3 w-8"><input type="checkbox" checked={selected.size === items.length && items.length > 0} onChange={toggleAll} data-testid="select-all"/></th>
              <th className="px-4 py-3">Title / Employer</th>
              <th className="px-4 py-3">Location</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Flags</th>
              <th className="px-4 py-3">Apps</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(j => (
              <tr key={j.id} className={`border-b border-slate-50 hover:bg-sky-50/30 ${selected.has(j.id) ? "bg-sky-50/60" : ""}`} data-testid={`admin-job-row-${j.id}`}>
                <td className="px-4 py-3"><input type="checkbox" checked={selected.has(j.id)} onChange={() => toggle(j.id)}/></td>
                <td className="px-4 py-3">
                  <div className="font-editorial font-semibold text-slate-900">{j.title}</div>
                  <div className="text-xs text-slate-500">{j.employer_name} · {j.subject || "—"}</div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">{[j.location_city, j.location_state].filter(Boolean).join(", ")}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{j.employment_type}<div className="text-slate-400">{j.work_mode}</div></td>
                <td className="px-4 py-3">
                  <select value={j.status} onChange={e => patch(j.id, { status: e.target.value })} className={`text-[11px] font-semibold px-2 py-1 rounded-full border border-slate-200 outline-none ${j.status === "active" ? "bg-emerald-50 text-emerald-700" : j.status === "expired" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`} data-testid={`admin-job-status-${j.id}`}>
                    <option value="active">active</option><option value="draft">draft</option><option value="expired">expired</option>
                  </select>
                </td>
                <td className="px-4 py-3 flex gap-1">
                  <button onClick={() => patch(j.id, { is_featured: !j.is_featured })} className={`p-1.5 rounded-lg ${j.is_featured ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-400"}`} title="Featured" data-testid={`admin-job-featured-${j.id}`}><Star className="w-3.5 h-3.5" fill={j.is_featured ? "currentColor" : "none"}/></button>
                  <button onClick={() => patch(j.id, { is_sponsored: !j.is_sponsored })} className={`p-1.5 rounded-lg ${j.is_sponsored ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-400"}`} title="Sponsored" data-testid={`admin-job-sponsored-${j.id}`}><Zap className="w-3.5 h-3.5" fill={j.is_sponsored ? "currentColor" : "none"}/></button>
                </td>
                <td className="px-4 py-3 text-sm font-semibold text-slate-800">{j.applications_count || 0}</td>
                <td className="px-4 py-3 text-right flex justify-end gap-1">
                  <Link to={`/job/${j.slug}`} target="_blank" className="p-2 rounded-lg bg-slate-100 hover:bg-sky-100 text-slate-600"><ExternalLink className="w-4 h-4"/></Link>
                  <button onClick={() => openEdit(j)} className="p-2 rounded-lg bg-slate-100 hover:bg-sky-100 text-slate-600" data-testid={`admin-job-edit-${j.id}`}><Edit3 className="w-4 h-4"/></button>
                  <button onClick={() => del(j.id)} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600" data-testid={`admin-job-del-${j.id}`}><Trash2 className="w-4 h-4"/></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="px-5 py-12 text-center text-slate-500">No jobs match your filter.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
